import type { Scenario } from '../types';

export const FIRST_NEGOTIATION: Scenario = {
  id: 'first-negotiation',
  title: 'the first negotiation',
  brief: 'a meet-up at a quiet bar. you have been chatting for a few weeks. tonight is the first real conversation about what you each want.',
  skill: 'articulating what you want and hearing what he wants without pre-committing',
  estMinutes: 6,
  startNode: 'open',
  nodes: {
    open: {
      id: 'open',
      paragraphs: [
        { text: 'the bar is half-empty; he chose a corner booth. he stands when you arrive and waits until you sit before he does.', speaker: 'narration' },
        { text: '"i\'m glad you said yes to meeting in person."', speaker: 'them', pause: 1000 },
        { text: 'you take a sip of the drink the server has just brought. the silence is comfortable for about three seconds.', speaker: 'narration' },
        { text: '"so. tell me what you\'re looking for."', speaker: 'them' },
      ],
      choices: [
        { text: 'name a specific thing you want, plainly.', nextNode: 'specific' },
        { text: 'defer: "honestly, i want to hear what you\'re looking for first."', nextNode: 'defer' },
        { text: 'name a curiosity — something you\'re drawn to but haven\'t committed to.', nextNode: 'curious' },
      ],
    },
    specific: {
      id: 'specific',
      paragraphs: [
        { text: 'you tell him directly. one thing you want, in unambiguous language. you watch his face as you say it.', speaker: 'narration' },
        { text: 'he holds your gaze. he does not look away. there is a small smile.', speaker: 'narration' },
        { text: '"that\'s a good answer. specific. how do you know you want that?"', speaker: 'them' },
      ],
      choices: [
        { text: 'tell him what experience or fantasy led you there.', nextNode: 'history' },
        { text: '"i don\'t fully. that\'s why i want to start small."', nextNode: 'small-version' },
      ],
    },
    defer: {
      id: 'defer',
      paragraphs: [
        { text: 'he tilts his head slightly. not annoyed. something more like noted.', speaker: 'narration' },
        { text: '"fair. but i\'ll ask again later. i want to know what you want, not what you think will fit me."', speaker: 'them' },
        { text: 'he talks then — clearly, without performing. he has thought about this. he names two things he wants and one thing he does not.', speaker: 'narration' },
      ],
      choices: [
        { text: 'thank him. tell him what you actually want now.', nextNode: 'specific' },
        { text: 'tell him you\'d like another conversation before answering.', nextNode: 'another-time' },
      ],
    },
    curious: {
      id: 'curious',
      paragraphs: [
        { text: '"i\'m curious about — " you say it. you say what you are curious about, and that you have not committed to it.', speaker: 'you' },
        { text: '"thank you for the distinction," he says. "curious is its own valid answer. it\'s not the same as yes."', speaker: 'them' },
      ],
      choices: [
        { text: 'ask him to tell you about his version of that thing.', nextNode: 'his-version' },
        { text: 'ask whether he tends to escalate fast or slow.', nextNode: 'pace-question' },
      ],
    },
    history: {
      id: 'history',
      paragraphs: [
        { text: 'you tell him a small story. not too much. just enough to show that this is not from nowhere.', speaker: 'narration' },
        { text: 'he listens with his whole face. when you finish, he waits a beat before responding.', speaker: 'narration' },
        { text: '"thank you. that helps me know what kind of yes you might be."', speaker: 'them' },
      ],
      choices: [
        { text: 'ask what he is looking for, now that you have shown your hand.', nextNode: 'his-turn' },
      ],
    },
    'small-version': {
      id: 'small-version',
      paragraphs: [
        { text: '"good," he says. "i want to know how you want to start. people who skip to the deep end usually drown."', speaker: 'them' },
      ],
      choices: [
        { text: 'tell him your version of small.', nextNode: 'his-turn' },
        { text: 'ask him for his version of small first.', nextNode: 'his-version' },
      ],
    },
    'another-time': {
      id: 'another-time',
      paragraphs: [
        { text: 'he nods. no sigh, no theater.', speaker: 'narration' },
        { text: '"of course. let\'s talk about the easy parts tonight and the harder parts when you\'ve thought about them. would that work?"', speaker: 'them' },
      ],
      choices: [
        { text: 'agree, and steer to easier topics.', nextNode: 'close' },
      ],
    },
    'his-version': {
      id: 'his-version',
      paragraphs: [
        { text: 'he tells you. specifically. with examples. you notice that he never insists.', speaker: 'narration' },
        { text: 'at one point he says: "i would want a clear no if any of this isn\'t the shape for you."', speaker: 'them' },
      ],
      choices: [
        { text: 'thank him for the specificity. say what fits and what doesn\'t.', nextNode: 'his-turn' },
        { text: 'tell him you need to sit with what he said before you can answer.', nextNode: 'another-time' },
      ],
    },
    'pace-question': {
      id: 'pace-question',
      paragraphs: [
        { text: '"slow," he says immediately. "the people who do this well move slow. fast usually means someone has skipped the boring parts."', speaker: 'them' },
      ],
      choices: [
        { text: 'tell him slow is what you want too.', nextNode: 'his-turn' },
        { text: 'ask what slow has looked like for him in the past.', nextNode: 'his-version' },
      ],
    },
    'his-turn': {
      id: 'his-turn',
      paragraphs: [
        { text: 'he tells you, then, what he wants. the conversation lengthens into the kind of talk that doesn\'t need refills.', speaker: 'narration' },
        { text: 'at the end he says: "let\'s not commit to anything tonight. let\'s see if both of us still want this in a week."', speaker: 'them' },
        { text: 'you say yes.', speaker: 'narration' },
      ],
      choices: [
        { text: 'walk out into the night.', nextNode: 'close' },
      ],
    },
    close: {
      id: 'close',
      paragraphs: [
        { text: 'on the walk home you notice that nothing was decided. and nothing was lost.', speaker: 'narration' },
        { text: 'that, you think, is what the first one is supposed to feel like.', speaker: 'narration' },
      ],
    },
  },
  reflection: {
    title: 'on the first negotiation',
    skillPracticed: 'articulating wants, hearing wants, and the discipline of not pre-committing',
    body: 'the first negotiation is a measurement, not a decision. you find out: can he listen? can he answer specifically? does he treat your curiosity differently from your yes? does he respect a pause? you find out whether the conversation feels like collaboration or like negotiation in the worst sense — bargaining over what you owe each other. healthy first negotiations leave both people with more information and no commitments. that is exactly what this one was for.',
    otherPaths: [
      'you could have led with what you did not want. naming a hard limit early is its own clarity.',
      'you could have asked him to describe how he treats partners between scenes. some of the most useful information lives in the boring questions.',
      'you could have steered the whole conversation to logistics — pace, communication, who calls who when — and saved the desire conversation for later.',
    ],
  },
};
